import React, { useMemo } from "react";
import { Form } from "react-final-form";
import { Box, Button, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import { DropzoneField, TextField } from "fond/form/fields";
import RequiredFiles from "fond/form/fields/RequiredFiles/RequiredFiles";
import { ProjectSubtype } from "fond/types";
import { getFilesFromEvent } from "fond/utils/files";
import { requireAll, required } from "fond/utils/validation";
import { Modal } from "fond/widgets";
import { validFileName } from "fond/widgets/Dropzone/utils";

import { getNBNFiles } from "./files";

interface UploadS18ModalProps {
  type: ProjectSubtype;
  projectCode?: string | null;
  onClose: () => void;
  onSubmit: (values: UploadFormData) => void;
}

export interface UploadFormData {
  ProjectCode: string;
  Files: File[];
}

const UploadS18Modal: React.FC<UploadS18ModalProps> = ({ type, projectCode, onClose, onSubmit }: UploadS18ModalProps) => {
  // The list of required files for the selected project type
  const s18Files = useMemo(() => getNBNFiles(type), [type]);

  return (
    <Modal
      open
      variant="primary"
      header="Upload input data"
      content={
        <Box mt={2} height="50%">
          <Form<UploadFormData>
            validateOnBlur
            onSubmit={onSubmit}
            subscription={{ submitting: true, valid: true, dirty: true }}
            render={({ handleSubmit, submitting, valid, dirty }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Box my={2} display="flex" flexDirection="column" rowGap={3} columnGap={6}>
                    <Box>
                      <TextField
                        inputProps={{ "data-testid": "new-scenario-name-input" }}
                        autoFocus
                        name="ProjectCode"
                        defaultValue={projectCode || "N2P"}
                        required
                        label="Project code"
                        fullWidth
                        validate={required}
                      />
                      <Typography variant="caption" color="textSecondary">
                        This value will be used to determine which design elements are in scope.
                      </Typography>
                    </Box>
                    <DropzoneField
                      name="Files"
                      acceptedFiles={{
                        "application/json": [".dat", ".id", ".map", ".tab"],
                        "application/zip": [".zip"],
                        // Windows uploads .zip files with the non-standard MIME type application/x-zip-compressed
                        // see e.g. https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types/Common_types
                        "application/x-zip-compressed": [".zip"],
                      }}
                      fullWidth
                      message="Upload ZIP or individual files by using drag and drop, or by clicking to browse."
                      dropzoneValidator={validFileName(s18Files.map(({ file }) => file))}
                      validate={requireAll(s18Files.length)}
                      getFilesFromEvent={getFilesFromEvent}
                    />
                  </Box>
                  <RequiredFiles fieldName="Files" fileMap={s18Files} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                      py: 3,
                    }}
                  >
                    <Button type="button" data-testid="report-cancel-button" color="primary" onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      data-testid="report-save-button"
                      color="primary"
                      loading={submitting}
                      disabled={!dirty || !valid || submitting}
                    >
                      Upload
                    </LoadingButton>
                  </Box>
                </form>
              );
            }}
          />
        </Box>
      }
      onClose={onClose}
      DialogActionsProps={{ sx: { display: "none" } }}
    />
  );
};

export default UploadS18Modal;
